// Console Admin di Studio (ORG_ADMIN): gestione di consulenti, aziende e
// assegnazioni LIMITATA alla propria organizzazione. Niente settings globali.
import { useCallback, useEffect, useState } from "react";
import { Landmark, UserPlus, Building2, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { assignmentRoleLabel } from "@/lib/strings";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ASSIGNMENT_ROLES = ["LEAD_CONSULTANT", "CONSULTANT", "REVIEWER", "SPECIALIST", "READ_ONLY"];

export default function OrgConsole() {
  const [overview, setOverview] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [invite, setInvite] = useState({ email: "", full_name: "" });
  const [company, setCompany] = useState({ denominazione: "", partita_iva: "", citta: "" });
  const [assign, setAssign] = useState({ consultant_user_id: "", company_id: "", consultant_role: "CONSULTANT" });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ov, cons, comps, asg] = await Promise.all([
        api.get("/org/overview"), api.get("/org/consultants"),
        api.get("/org/companies"), api.get("/org/assignments")]);
      setOverview(ov.data);
      setConsultants(cons.data.items || []);
      setCompanies(comps.data.items || []);
      setAssignments(asg.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inviteConsultant = async () => {
    try {
      const { data } = await api.post("/org/consultants", invite);
      if (data.invitation?.email_sent) {
        toast.success(`Invito inviato via email a ${invite.email}`);
      } else {
        toast.success("Invito creato", {
          description: `Link: ${window.location.origin}${data.activation_link}` });
      }
      setInvite({ email: "", full_name: "" });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/org/consultants/${id}/status`, { status });
      toast.success(status === "ACTIVE" ? "Consulente riattivato" : "Consulente sospeso");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const createCompany = async () => {
    try {
      const { data } = await api.post("/org/companies", company);
      toast.success(`${data.company.denominazione} creata in PENDING_SETUP`,
        data.activation_link
          ? { description: `Invito admin: ${window.location.origin}${data.activation_link}` }
          : undefined);
      setCompany({ denominazione: "", partita_iva: "", citta: "" });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const createAssignment = async () => {
    try {
      await api.post("/org/assignments", assign);
      toast.success("Assegnazione creata");
      setAssign({ consultant_user_id: "", company_id: "", consultant_role: "CONSULTANT" });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const endAssignment = async (id) => {
    try {
      await api.put(`/org/assignments/${id}/end`);
      toast.success("Assegnazione chiusa");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="org-console-error">{error}</p>;
  if (!overview) return <p className="text-sm text-slate-600" data-testid="org-console-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="org-console-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Landmark className="w-7 h-7 text-green-800" aria-hidden="true" />
          {overview.organization?.name || "Il mio Studio"}
        </h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="org-console-stats">
          {overview.consultants_count} consulenti · {overview.companies_count} aziende clienti ·{" "}
          {overview.pending_invitations} inviti in attesa
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="org-consultants-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4" aria-hidden="true" /> Consulenti dello studio
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Nome e cognome" value={invite.full_name}
                data-testid="org-invite-name"
                onChange={(e) => setInvite((f) => ({ ...f, full_name: e.target.value }))} />
              <Input placeholder="Email" type="email" value={invite.email}
                data-testid="org-invite-email"
                onChange={(e) => setInvite((f) => ({ ...f, email: e.target.value }))} />
              <Button size="sm" className="bg-green-700 hover:bg-green-800"
                disabled={!invite.email || invite.full_name.length < 2}
                onClick={inviteConsultant} data-testid="org-invite-submit">Invita</Button>
            </div>
            <div className="space-y-1.5">
              {consultants.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                  data-testid={`org-consultant-${c.id}`}>
                  <span className="font-medium text-slate-800 flex-1">{c.full_name}</span>
                  <span className="text-slate-500">{c.email}</span>
                  <Badge variant="outline" className={c.status === "SUSPENDED"
                    ? "bg-red-50 text-red-700 border-red-300"
                    : "bg-green-50 text-green-800 border-green-300"}
                    data-testid={`org-consultant-status-${c.id}`}>
                    {c.status || "ACTIVE"}</Badge>
                  {c.status === "SUSPENDED" ? (
                    <Button size="sm" variant="outline" className="h-6 text-[11px]"
                      onClick={() => setStatus(c.id, "ACTIVE")}
                      data-testid={`org-consultant-activate-${c.id}`}>Riattiva</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-6 text-[11px] text-red-700 border-red-200"
                      onClick={() => setStatus(c.id, "SUSPENDED")}
                      data-testid={`org-consultant-suspend-${c.id}`}>Sospendi</Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="org-companies-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" aria-hidden="true" /> Aziende dello studio
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Denominazione *" value={company.denominazione}
                data-testid="org-company-name"
                onChange={(e) => setCompany((f) => ({ ...f, denominazione: e.target.value }))} />
              <Input placeholder="P.IVA" value={company.partita_iva}
                data-testid="org-company-piva"
                onChange={(e) => setCompany((f) => ({ ...f, partita_iva: e.target.value }))} />
              <div className="flex gap-2">
                <Input placeholder="Città" value={company.citta}
                  data-testid="org-company-city"
                  onChange={(e) => setCompany((f) => ({ ...f, citta: e.target.value }))} />
                <Button size="sm" className="bg-green-700 hover:bg-green-800"
                  disabled={company.denominazione.length < 2}
                  onClick={createCompany} data-testid="org-company-submit">Crea</Button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {companies.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                  data-testid={`org-company-${c.id}`}>
                  <span className="font-medium text-slate-800 flex-1">{c.denominazione}</span>
                  <span className="text-slate-500">{c.partita_iva || "—"}</span>
                  <Badge variant="outline" className="text-[9px]">{c.status}</Badge>
                </div>
              ))}
              {companies.length === 0 && (
                <p className="text-xs text-slate-500">Nessuna azienda ancora associata.</p>)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="org-assignments-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4" aria-hidden="true" /> Assegnazioni
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Consulente</Label>
              <Select value={assign.consultant_user_id}
                onValueChange={(v) => setAssign((f) => ({ ...f, consultant_user_id: v }))}>
                <SelectTrigger className="w-56 h-9" data-testid="org-assign-consultant">
                  <SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {consultants.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Azienda</Label>
              <Select value={assign.company_id}
                onValueChange={(v) => setAssign((f) => ({ ...f, company_id: v }))}>
                <SelectTrigger className="w-56 h-9" data-testid="org-assign-company">
                  <SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ruolo</Label>
              <Select value={assign.consultant_role}
                onValueChange={(v) => setAssign((f) => ({ ...f, consultant_role: v }))}>
                <SelectTrigger className="w-44 h-9" data-testid="org-assign-role">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_ROLES.map((r) => <SelectItem key={r} value={r}>{assignmentRoleLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" className="bg-green-700 hover:bg-green-800 h-9"
              disabled={!assign.consultant_user_id || !assign.company_id}
              onClick={createAssignment} data-testid="org-assign-submit">Assegna</Button>
          </div>
          <div className="space-y-1.5">
            {assignments.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`org-assignment-${a.id}`}>
                <Badge variant="outline" className="text-[9px]">{assignmentRoleLabel(a.consultant_role)}</Badge>
                <span className="font-medium text-slate-800">{a.company_name}</span>
                <Badge variant="outline" className={a.status === "ACTIVE"
                  ? "bg-green-50 text-green-800 border-green-300"
                  : "bg-slate-100 text-slate-500 border-slate-300"}>{a.status}</Badge>
                {a.status === "ACTIVE" && (
                  <Button size="sm" variant="outline" className="h-6 text-[11px]"
                    onClick={() => endAssignment(a.id)}
                    data-testid={`org-assignment-end-${a.id}`}>Chiudi</Button>
                )}
              </div>
            ))}
            {assignments.length === 0 && (
              <p className="text-xs text-slate-500">Nessuna assegnazione.</p>)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
